import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { IconName } from '@shared/components/core/images/IconMap';
import Icons from '@shared/components/core/images/Icons';
import { getKeyDisplayname, getKeyIcon } from '@shared/constants/Keybindings';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { MacroDisplayEvent } from '@shared/models/deviceProfiles/MacroEvent';
import { useTranslation } from 'react-i18next';

const otherEventTypeToIconMap: Record<string, { title: string; icon: IconName }> = {
    MouseMove: { title: 'Move the mouse', icon: 'computerMouse' },
    JoysticksMove: { title: 'Move the joystick', icon: 'joystick' },
    PlayAudio: { title: 'Speak', icon: 'speaker' },
    Type: { title: 'Type', icon: 'keyboard' },
    Pause: { title: 'Pause', icon: 'timer' },
    StopOutputs: { title: 'Stop All', icon: 'stop' },
    DeviceTypeCustomAction: { title: 'Custom', icon: 'hub' },
};
interface MacroStepIconsProps {
    event: MacroDisplayEvent;
    overflow?: boolean;
    iconSize?: number;
}

export default function MacroStepIcons({ event, iconSize = 28 }: Readonly<MacroStepIconsProps>) {
    const { t } = useTranslation();
    const defaultLocale = useEditProfileStore((state) => state.defaultLocale);
    const getOtherView = (step: MacroDisplayEvent) => {
        const eventType = step.eventType;
        const eventInfo = otherEventTypeToIconMap[eventType];
        const { title, icon } = eventInfo;

        return (
            <Tooltip title={title}>
                <SingleStepContainer>
                    <Icons name={icon} size={iconSize} />
                </SingleStepContainer>
            </Tooltip>
        );
    };

    const getOverflowView = () => {
        return (
            <Tooltip title={t('common:event.expand')}>
                <SingleStepContainer>
                    <Icons name="layers" size={iconSize} />
                </SingleStepContainer>
            </Tooltip>
        );
    };

    const getKeysView = (step: MacroDisplayEvent) => {
        let eventTypeDescription: string;
        if (step.eventType === 'KeyToggle') {
            eventTypeDescription = t('common:event.keyToggle.short');
        } else if (step.eventType === 'KeyRelease') {
            eventTypeDescription = t('common:event.keyRelease.short');
        } else {
            eventTypeDescription = t('common:event.keyPress.short');
        }

        const keysDescription = step.keys?.map((k) => getKeyDisplayname(k, defaultLocale))?.join(' · ') ?? '';

        return (
            <Tooltip title={`${eventTypeDescription} ${keysDescription}`}>
                <SingleStepContainer>
                    {step.keys?.slice(0, 2).map((key, index) => (
                        <Icons key={index} name={getKeyIcon(key, defaultLocale)} size={iconSize} />
                    ))}
                    {step.keys && step.keys.length > 2 && getOverflowView()}
                </SingleStepContainer>
            </Tooltip>
        );
    };

    return (
        <>
            {event?.eventType === 'KeyPress' || event?.eventType == 'KeyRelease' || event?.eventType == 'KeyToggle'
                ? getKeysView(event)
                : getOtherView(event)}
        </>
    );
}

const SingleStepContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;
